import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { markAllFormFieldsAsTouched } from '@mt-ng2/common-functions';
import { DeliveryService } from '../services/delivery.service';
import { IDelivery } from '../../model/interfaces/delivery';
import { DeliveryDynamicConfig } from '../delivery.dynamic-config';
import { finalize } from 'rxjs/operators';
import { DynamicField, DynamicLabel, IDynamicFormConfig } from '@mt-ng2/dynamic-form';
import { IDeliveryType } from '../../model/interfaces/delivery-type';
import { forkJoin, Subscription } from 'rxjs';
import { ITrailerType } from '../../model/interfaces/trailer-type';
import { CommonService } from '../../common/services/common.service';
import { ICountry } from '../../model/interfaces/country';
import { CustomerService } from '../../customers/customer.service';
import { ICustomer } from '../../model/interfaces/customer';
import { IDeliveryEquipmentType } from '../../model/interfaces/delivery-equipment-type';
import { CarrierService } from '../../carriers/services/carrier.service';
import { ICarrier } from '../../model/interfaces/carrier';
import { AuthService, ILoggedIn } from '@mt-ng2/auth-module';
import { OrderService } from '../../orders/order.service';
import { DeliveryEquipmentTypes } from '../../model/DeliveryEquipmentTypes';
import { WarehouseService } from '../../warehouses/services/warehouse.service';
import { IWarehouse } from '../../model/interfaces/warehouse';
import { UserRoles } from '../../model/UserRoles';
import { UserService } from '../../users/user.service';
import { DeliveryStatuses } from '../../model/DeliveryStatuses';
import { DeliveryTypes } from '../../model/DeliveryTypes';

@Component({
    selector: 'app-delivery-basic-info',
    templateUrl: './delivery-basic-info.component.html',
    styleUrls: ['./delivery-basic-info.component.css'],
    standalone: false
})
export class DeliveryBasicInfoComponent implements OnInit, OnDestroy {
    @Input() delivery!: IDelivery;
    @Input() canEdit = false;
    @Input() canAdd = false;

    isEditing = false;
    isHovered = false;
    viewOnly: DynamicLabel[] = [];
    formObject: DynamicField[] = [];
    formFactory!: DeliveryDynamicConfig<IDelivery>;
    doubleClickIsDisabled = false;
    deliveryTypes: IDeliveryType[] = [];
    deliveryEquipmentTypes: IDeliveryEquipmentType[] = [];
    trailerTypes: ITrailerType[] = [];
    countries: ICountry[] = [];
    customers: ICustomer[] = [];
    carriers: ICarrier[] = [];
    warehouses: IWarehouse[] = [];
    authUserRole = 0;
    subscription: Subscription = new Subscription();
    loggedIn!: ILoggedIn;
    showAdditionalContainers = false;
    additionalContainerNumbers: string[] = [];
    showContainerNote = false;

    private formGroup: UntypedFormGroup | null = null;
    private plusBtnUnlistenFn: (() => void) | null = null;

    constructor(
        private deliveryService: DeliveryService,
        private notificationsService: NotificationsService,
        private router: Router,
        private commonService: CommonService,
        private customerService: CustomerService,
        private carrierService: CarrierService,
        private authService: AuthService,
        private orderService: OrderService,
        private warehouseService: WarehouseService,
        private userService: UserService,
        private el: ElementRef,
        private renderer: Renderer2,
    ) {}

    ngOnInit(): void {
        this.isEditing = false;
        this.loggedIn = this.authService.currentUser.getValue();
        this.subscription.add(
            this.orderService.getAuthUserRole(this.loggedIn.AuthId).subscribe((authUserRole) => {
                this.authUserRole = authUserRole;
                this.subscription.add(
                    forkJoin([
                        this.deliveryService.getSimpleDeliveryTypes(),
                        this.deliveryService.getSimpleDeliveryEquipmentTypes(),
                        this.deliveryService.getSimpleTrailerTypes(),
                        this.commonService.getCountries(),
                        this.customerService.getActiveCustomers(),
                        this.carrierService.getSimpleCarriers(),
                        this.warehouseService.getUserWarehouses(),
                    ]).subscribe(([deliveryTypes, deliveryEquipmentTypes, trailerTypes, countries, customers, carriers, warehouses]) => {
                        this.deliveryTypes = deliveryTypes;
                        this.deliveryEquipmentTypes = deliveryEquipmentTypes;
                        this.trailerTypes = trailerTypes;
                        this.countries = countries;
                        this.customers = customers;
                        this.carriers = carriers;
                        this.warehouses = warehouses;
                        this.setConfig();
                    }),
                );
            }),
        );
    }

    ngOnDestroy(): void {
        this.teardownContainerUi();
        this.subscription.unsubscribe();
        this.formGroup = null;
    }

    setConfig(): void {
        let config: IDynamicFormConfig;
        this.formFactory = new DeliveryDynamicConfig<IDelivery>(
            this.delivery,
            this.deliveryTypes,
            this.deliveryEquipmentTypes,
            this.trailerTypes,
            this.countries,
            this.customers,
            this.carriers,
            this.warehouses,
            this.authUserRole,
        );

        if (this.delivery.Id === 0) {
            // new delivery
            this.isEditing = true;
            config = this.formFactory.getForCreate();
        } else {
            // existing delivery
            config = this.formFactory.getForUpdate();
        }

        this.viewOnly = config?.viewOnly?.map((x) => new DynamicLabel(x)) ?? [];
        this.formObject = config.formObject?.map((x) => new DynamicField(x));
    }

    edit(): void {
        if (this.canEdit) {
            this.isEditing = true;
        }
    }

    cancelClick(): void {
        if (this.delivery.Id === 0) {
            void this.router.navigate(['/deliveries']);
        } else {
            this.isEditing = false;
        }
    }

    formSubmitted(form: UntypedFormGroup): void {
        this.clearContainerInputHighlights();

        if (!form.valid) {
            markAllFormFieldsAsTouched(form);
            this.error();
            return;
        }

        const formValues = form.value.Delivery as IDelivery;
        this.formFactory.assignFormValues(this.delivery, formValues);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const confirmedDeliveryDate = this.delivery.ConfirmedDeliveryDate;
        if (confirmedDeliveryDate && confirmedDeliveryDate < today) {
            this.notificationsService.error('Confirmed Delivery Date cannot be set to a past date.');
            return;
        }

        // Not sure why but "assignFormValues" isn't working for these form value assignments.
        this.delivery.DeliveryEquipmentTypeId = formValues.DeliveryEquipmentTypeId;
        this.delivery.DeliveryTypeId = formValues.DeliveryTypeId;
        this.delivery.TrailerTypeId = formValues.TrailerTypeId;
        this.delivery.WarehouseId = formValues.WarehouseId;
        this.delivery.CountryCode = formValues.CountryCode;
        this.delivery.ContainerNumber = formValues.ContainerNumber;
        this.delivery.TrailerNumber = formValues.TrailerNumber;
        this.delivery.Temperature = formValues.Temperature;

        if (this.delivery.CountryCode === '') this.delivery.CountryCode = undefined;

        if (this.delivery.DeliveryTypeId && this.delivery.DeliveryTypeId <= 0) this.delivery.DeliveryTypeId = undefined;

        if (this.delivery.TrailerTypeId && this.delivery.TrailerTypeId <= 0) this.delivery.TrailerTypeId = undefined;

        if (!this.delivery.Id || this.delivery.Id === 0) {
            const primaryContainerNumber = (this.delivery.ContainerNumber ?? '').trim();
            const extraContainers = this.additionalContainerNumbers.map((c) => (c ?? '').trim());

            if (extraContainers.length > 0 && !primaryContainerNumber) {
                this.highlightAndFocusPrimaryContainerInput();
                this.notificationsService.error('Primary container number is required.');
                return;
            }

            const emptyExtraIndexes = extraContainers
                .map((value, index) => ({ value, index }))
                .filter((x) => x.value.length === 0)
                .map((x) => x.index);

            if (emptyExtraIndexes.length > 0) {
                this.highlightAndFocusExtraContainerInputs(emptyExtraIndexes);
                this.notificationsService.error('Please enter a container number or remove the empty field.');
                return;
            }

            if (extraContainers.length > 0) {
                const allContainerNumbers = [primaryContainerNumber, ...extraContainers];

                const normalizedContainerNumbers = allContainerNumbers.map((c) => c.toLowerCase());
                if (new Set(normalizedContainerNumbers).size !== normalizedContainerNumbers.length) {
                    const duplicatedValues = normalizedContainerNumbers.filter((value, index, arr) => arr.indexOf(value) !== index);
                    if (duplicatedValues.includes(normalizedContainerNumbers[0])) {
                        this.highlightAndFocusPrimaryContainerInput();
                    }

                    const duplicatedExtraIndexes = extraContainers
                        .map((value, index) => ({ value: value.toLowerCase(), index }))
                        .filter((x) => duplicatedValues.includes(x.value))
                        .map((x) => x.index);
                    this.highlightAndFocusExtraContainerInputs(duplicatedExtraIndexes);

                    this.notificationsService.error('Duplicate container numbers are not allowed in the same form.');
                    return;
                }

                const creates = allContainerNumbers.map((containerNumber) =>
                    this.deliveryService.create({ ...this.delivery, ContainerNumber: containerNumber, Id: 0 }),
                );
                forkJoin(creates)
                    .pipe(finalize(() => (this.doubleClickIsDisabled = false)))
                    .subscribe((ids) => {
                        forkJoin(ids.map((id) => this.deliveryService.getById(id))).subscribe((deliveries) => {
                            const numbers = deliveries.map((d) => d.DeliveryNumber).join(', ');
                            this.notificationsService.success(`${ids.length} deliveries created — Delivery Numbers: ${numbers}`);
                            void this.router.navigate(['/deliveries']);
                            this.deliveryService.emitChange(this.delivery);
                        });
                    });
            } else {
                // handle new delivery save
                this.deliveryService
                    .create(this.delivery)
                    .pipe(finalize(() => (this.doubleClickIsDisabled = false)))
                    .subscribe((answer) => {
                        void this.router.navigate([`/deliveries/${answer}`]);
                        this.success();
                        this.deliveryService.emitChange(this.delivery);
                    });
            }
        } else {
            // handle existing delivery save
            this.changeDeliveryStatusIfNeeded();
            this.deliveryService
                .updateWithFks(this.delivery)
                .pipe(finalize(() => (this.doubleClickIsDisabled = false)))
                .subscribe(() => {
                    this.isEditing = false;
                    this.success();
                    this.deliveryService.emitChange(this.delivery);
                    this.setConfig();
                });
        }
    }

    error(): void {
        this.notificationsService.error('Save failed.  Please check the form and try again.');
    }

    success(): void {
        this.notificationsService.success('Delivery saved successfully.');
    }

    formCreated(event: UntypedFormGroup): void {
        this.formGroup = event;
        const deliveryEquipmentTypeControl = this.getRequiredControl(event, 'Delivery.DeliveryEquipmentTypeId');
        this.subscription.add(
            deliveryEquipmentTypeControl.valueChanges.subscribe((answer: number) => {
                if (answer === DeliveryEquipmentTypes.Container) {
                    this.setContainerFields(event);
                    if (this.delivery.Id === 0) {
                        this.setupContainerUi();
                    }
                    return;
                }

                this.teardownContainerUi();

                if (answer === DeliveryEquipmentTypes.FacilitySupplies || answer === DeliveryEquipmentTypes.TrailerFlatbedOther) {
                    this.setTrailerFields(event);
                    return;
                }

                this.setNonContainerNonTrailerFields(event);
            }),
        );

        this.subscription.add(
            this.userService.getById(this.loggedIn.Id).subscribe({
                next: (user) => {
                    const carrierControl = this.getRequiredControl(event, 'Delivery.CarrierId');
                    const customerControl = this.getRequiredControl(event, 'Delivery.CustomerId');
                    if (this.authUserRole === UserRoles.Carrier) {
                        carrierControl.setValue(user.Contact?.CarrierId);
                        carrierControl.disable();
                    }

                    if (this.authUserRole === UserRoles.Customer) {
                        customerControl.setValue(user.Contact?.CustomerId);
                        customerControl.disable();
                    }
                },
            }),
        );
    }

    addContainerNumber(): void {
        if (!this.formGroup) return;
        const mainValue = ((this.formGroup.get('Delivery.ContainerNumber')?.value as string) ?? '').trim();
        if (!mainValue) return;
        this.clearContainerInputHighlights();
        if (this.additionalContainerNumbers.length === 0) {
            this.showContainerNote = true;
        }
        this.additionalContainerNumbers.push('');
    }

    removeContainerNumber(index: number): void {
        this.clearContainerInputHighlights();
        this.additionalContainerNumbers.splice(index, 1);
        if (this.additionalContainerNumbers.length === 0) {
            this.showContainerNote = false;
        }
    }

    trackByIndex(index: number): number {
        return index;
    }

    private setupContainerUi(): void {
        this.showAdditionalContainers = true;
        this.additionalContainerNumbers = [];
        this.showContainerNote = false;
        setTimeout(() => {
            const hostEl = this.el.nativeElement as HTMLElement;
            const fields = Array.from(hostEl.querySelectorAll('.dynamic-field.form-input'));
            let containerField: Element | null = null;
            for (const field of fields) {
                const label = field.querySelector('label');
                if (label?.textContent?.trim().includes('Container Number')) {
                    containerField = field;
                    break;
                }
            }
            if (!containerField) return;

            // Inject + button beside the input as input-group-btn (only once)
            const inputEl = containerField.querySelector('input') as HTMLElement | null;
            const inputSpan = inputEl?.parentElement;
            if (inputSpan && !inputSpan.querySelector('.container-add-btn')) {
                this.renderer.addClass(inputSpan, 'input-group');
                this.renderer.addClass(inputSpan, 'container-input-row');

                const btnGroupSpan: HTMLElement = this.renderer.createElement('span');
                this.renderer.addClass(btnGroupSpan, 'input-group-btn');

                const plusBtn: HTMLElement = this.renderer.createElement('button');
                this.renderer.setAttribute(plusBtn, 'type', 'button');
                this.renderer.addClass(plusBtn, 'btn');
                this.renderer.addClass(plusBtn, 'btn-flat');
                this.renderer.addClass(plusBtn, 'btn-success');
                this.renderer.addClass(plusBtn, 'container-add-btn');
                this.renderer.setAttribute(plusBtn, 'title', 'Add another container number');
                const icon: HTMLElement = this.renderer.createElement('i');
                this.renderer.addClass(icon, 'fa');
                this.renderer.addClass(icon, 'fa-plus');
                this.renderer.appendChild(plusBtn, icon);
                this.plusBtnUnlistenFn = this.renderer.listen(plusBtn, 'click', () => this.addContainerNumber());
                this.renderer.appendChild(btnGroupSpan, plusBtn);
                this.renderer.appendChild(inputSpan, btnGroupSpan);
            }

            // Move the extra-containers-section div to right after the container field
            // Using [hidden] (not *ngIf) means Angular never removes this element, so
            // the DOM position we set here persists for the lifetime of the component.
            const section = hostEl.querySelector('.extra-containers-section');
            const parent = containerField.parentElement;
            if (section && parent) {
                this.renderer.insertBefore(parent, section, containerField.nextSibling);
            }
        });
    }

    private teardownContainerUi(): void {
        this.showAdditionalContainers = false;
        this.additionalContainerNumbers = [];
        this.showContainerNote = false;
        this.clearContainerInputHighlights();

        if (this.plusBtnUnlistenFn) {
            this.plusBtnUnlistenFn();
            this.plusBtnUnlistenFn = null;
        }

        const hostEl = this.el.nativeElement as HTMLElement;
        const fields = Array.from(hostEl.querySelectorAll('.dynamic-field.form-input'));
        for (const field of fields) {
            const label = field.querySelector('label');
            if (label?.textContent?.trim().includes('Container Number')) {
                const inputEl = field.querySelector('input') as HTMLElement | null;
                const inputSpan = inputEl?.parentElement;
                if (inputSpan) {
                    const btnGroup = inputSpan.querySelector('.input-group-btn');
                    if (btnGroup) this.renderer.removeChild(inputSpan, btnGroup);
                    this.renderer.removeClass(inputSpan, 'container-input-row');
                    this.renderer.removeClass(inputSpan, 'input-group');
                }
                break;
            }
        }
    }

    private setContainerFields(event: UntypedFormGroup): void {
        const containerNumberControl = this.getRequiredControl(event, 'Delivery.ContainerNumber');
        const deliveryTypeControl = this.getRequiredControl(event, 'Delivery.DeliveryTypeId');
        const trailerNumberControl = this.getRequiredControl(event, 'Delivery.TrailerNumber');
        const trailerTypeControl = this.getRequiredControl(event, 'Delivery.TrailerTypeId');

        containerNumberControl.enable();
        containerNumberControl.mtSetRequired(true);

        deliveryTypeControl.enable();
        deliveryTypeControl.mtSetRequired(true);

        trailerNumberControl.disable();
        trailerNumberControl.mtSetRequired(false);

        trailerTypeControl.disable();
        trailerTypeControl.setValue(null);
        trailerTypeControl.mtSetRequired(false);
    }

    private setTrailerFields(event: UntypedFormGroup): void {
        const trailerNumberControl = this.getRequiredControl(event, 'Delivery.TrailerNumber');
        const deliveryTypeControl = this.getRequiredControl(event, 'Delivery.DeliveryTypeId');
        const trailerTypeControl = this.getRequiredControl(event, 'Delivery.TrailerTypeId');
        const containerNumberControl = this.getRequiredControl(event, 'Delivery.ContainerNumber');

        trailerNumberControl.enable();
        trailerNumberControl.mtSetRequired(true);

        deliveryTypeControl.enable();
        deliveryTypeControl.mtSetRequired(true);

        trailerTypeControl.enable();
        trailerTypeControl.mtSetRequired(true);

        containerNumberControl.disable();
        containerNumberControl.mtSetRequired(false);
    }

    private setNonContainerNonTrailerFields(event: UntypedFormGroup): void {
        const deliveryTypeControl = this.getRequiredControl(event, 'Delivery.DeliveryTypeId');
        const trailerTypeControl = this.getRequiredControl(event, 'Delivery.TrailerTypeId');
        const trailerNumberControl = this.getRequiredControl(event, 'Delivery.TrailerNumber');
        const containerNumberControl = this.getRequiredControl(event, 'Delivery.ContainerNumber');

        deliveryTypeControl.disable();
        deliveryTypeControl.setValue(null);
        deliveryTypeControl.mtSetRequired(false);

        trailerTypeControl.disable();
        trailerTypeControl.setValue(null);
        trailerTypeControl.mtSetRequired(false);

        trailerNumberControl.disable();
        trailerNumberControl.mtSetRequired(false);

        containerNumberControl.disable();
        containerNumberControl.mtSetRequired(false);
    }

    private getRequiredControl(form: UntypedFormGroup, path: string): AbstractControl & { mtSetRequired: (required: boolean) => void } {
        const control = form.get(path);
        if (!control) {
            throw new Error(`Missing form control: ${path}`);
        }
        return control as AbstractControl & { mtSetRequired: (required: boolean) => void };
    }

    private clearContainerInputHighlights(): void {
        const hostEl = this.el.nativeElement as HTMLElement;
        const highlightedInputs = Array.from(hostEl.querySelectorAll<HTMLInputElement>('.container-input-error-highlight'));
        for (const input of highlightedInputs) {
            this.renderer.removeClass(input, 'container-input-error-highlight');
            this.renderer.removeStyle(input, 'border-color');
            this.renderer.removeStyle(input, 'box-shadow');
        }
    }

    private highlightAndFocusPrimaryContainerInput(): void {
        const hostEl = this.el.nativeElement as HTMLElement;
        const fields = Array.from(hostEl.querySelectorAll('.dynamic-field.form-input'));
        for (const field of fields) {
            const label = field.querySelector('label');
            if (!label?.textContent?.trim().includes('Container Number')) {
                continue;
            }

            const input = field.querySelector('input');
            if (!input) {
                return;
            }

            this.applyContainerInputErrorHighlight(input);
            input.focus();
            return;
        }
    }

    private highlightAndFocusExtraContainerInputs(indexes: number[]): void {
        if (indexes.length === 0) {
            return;
        }

        const hostEl = this.el.nativeElement as HTMLElement;
        const inputs = Array.from(hostEl.querySelectorAll<HTMLInputElement>('.extra-container-row input.form-control'));

        for (const index of indexes) {
            const input = inputs[index];
            if (input) {
                this.applyContainerInputErrorHighlight(input);
            }
        }

        const firstInput = inputs[indexes[0]];
        if (firstInput) {
            firstInput.focus();
        }
    }

    private applyContainerInputErrorHighlight(input: HTMLInputElement): void {
        this.renderer.addClass(input, 'container-input-error-highlight');
        this.renderer.setStyle(input, 'border-color', '#d9534f');
        this.renderer.setStyle(input, 'box-shadow', '0 0 0 1px #d9534f inset');
    }

    private changeDeliveryStatusIfNeeded(): void {
        // If the delivery is Confirmed and is cargo for warehouse and has a data file, set status to ReadyForCheckIn
        const isConfirmed = this.delivery.StatusId === DeliveryStatuses.Confirmed;
        const isCargoForWarehouse = this.delivery.DeliveryTypeId === DeliveryTypes.CargoForWarehouse;
        if (isConfirmed && isCargoForWarehouse && this.delivery.DataFile) {
            this.delivery.StatusId = DeliveryStatuses.ReadyForCheckIn;
            return;
        }

        // If the delivery is Confirmed and is cargo for fumigation and has a data file or CoC, set status to ReadyForCheckIn
        const isCargoForFumigation = this.delivery.DeliveryTypeId === DeliveryTypes.CargoForFumigation;
        if (isConfirmed && isCargoForFumigation && (this.delivery.DataFile || this.delivery.Coc)) {
            this.delivery.StatusId = DeliveryStatuses.ReadyForCheckIn;
        }

        // If the delivery is ReadyForCheckIn and is cargo for warehouse and doesn't have a data file, set status to confirmed
        const isReadyForCheckIn = this.delivery.StatusId === DeliveryStatuses.ReadyForCheckIn;
        if (isReadyForCheckIn && isCargoForWarehouse && !this.delivery.DataFile) {
            this.delivery.StatusId = DeliveryStatuses.Confirmed;
            return;
        }

        // If the delivery is ReadyForCheckIn and is cargo for fumigation and doesn't have a data file or CoC, set status to confirmed
        if (isReadyForCheckIn && isCargoForFumigation && (!this.delivery.DataFile || !this.delivery.Coc)) {
            this.delivery.StatusId = DeliveryStatuses.Confirmed;
        }
    }
}
